import { restartableTask } from 'ember-concurrency';
import { query as queryBuilder } from '@warp-drive/utilities/json-api';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import embedPdf from '../../../modifiers/embed-pdf.ts';
import Footer from 'tio-ui/components/footer';
import Modal from 'tio-ui/components/modal';
import type { RecommendationExtension as RecommendationModel } from 'tio-data/data/resources/recommendation.ext';
import type { Store as StoreService } from '@warp-drive/core';
import { t } from 'ember-intl';
import type { UserExtension as UserModel } from 'tio-data/data/resources/user.ext';
import Table from 'tio-ui/components/table';
import safeFormatDate from '../../../helpers/safe-format-date.ts';
import TioPreviewButton from '../../tio/preview-button.gts';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { buildTioAuthHeaders } from 'tio-data/utils/auth-headers';
import type FeaturesService from '../../../services/features.ts';

interface ProfileRecommendationsTableSignature {
  Args: {
    recommendationType: string;
    user: UserModel;
    limit?: number;
  };
}

export default class ProfileRecommendationsTableComponent extends Component<ProfileRecommendationsTableSignature> {
  @service declare store: StoreService;
  @service declare features: FeaturesService;

  @tracked showPreview = false;
  @tracked pdfSrc = '';

  // WarpDrive migration Phase 2.4: buildTioAuthHeaders replaces adapter.headers
  // for direct fetch calls (PDF blob download — cannot go through store.request).
  get tioAuthHeaders() {
    return buildTioAuthHeaders({
      apiKey: this.features.ENV?.apiKey ?? '',
    });
  }

  // WarpDrive migration Phase 2.4: buildBaseURL replaces adapter.host for
  // direct fetch calls — reads globally-configured host (setBuildURLConfig).
  get recommendationsBaseUrl() {
    const opts: QueryUrlOptions = {
      identifier: { type: 'recommendations' },
      op: 'query',
      resourcePath: 'recommendations',
    };
    return buildBaseURL(opts);
  }

  showPreviewModal = async (recommendation: RecommendationModel) => {
    const res = await fetch(`${this.recommendationsBaseUrl}/${recommendation.id}.pdf`, {
      headers: this.tioAuthHeaders,
    });
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    this.pdfSrc = url;
    this.showPreview = true;
  };

  closePreviewModal = () => {
    this.showPreview = false;
    this.pdfSrc = '';
  };

  get results(): RecommendationModel[] {
    return this.data.value || [];
  }

  get userId() {
    return this.args.user?.id;
  }

  get recommendationType() {
    return this.args.recommendationType;
  }

  data = trackedFunction(this, async () => {
    // @ts-expect-error: Task return type not properly inferred
    const results = await this.loadRecords.perform(this.userId, this.recommendationType);
    return results;
  });

  get isLoading() {
    return this.data.isLoading;
  }

  loadRecords = restartableTask(async (userId: string, recommendationType: string) => {
    if (!userId) return;

    const page = {
      limit: this.args.limit || 10,
      offset: 0,
    };

    const filter = {
      user: userId,
      'requesting-app': recommendationType,
    };

    const sort = '-created-at';

    // WarpDrive builder read (Phase 2.4/2.6) — replaces store.query;
    // flattened bracket keys satisfy the builder's QueryParamsSource type
    // (identical wire serialization).
    const { content } = (await this.store.request(
      queryBuilder<RecommendationModel>('recommendation', {
        'filter[user]': filter.user,
        'filter[requesting-app]': filter['requesting-app'],
        'page[limit]': page.limit,
        'page[offset]': page.offset,
        sort,
      })
    )) as { content: { data: RecommendationModel[] } };
    return content.data;
  });

  <template>
    <Table class="p-4" @isLoading={{this.isLoading}}>
      <:thead as |options|>
        <options.tr>
          <options.th />
          <options.th>{{t "common.recommendations.date"}}</options.th>
          <options.th>{{t "common.recommendations.results"}}</options.th>
        </options.tr>
      </:thead>
      <:tbody as |options|>
        {{#each this.results as |result|}}
          <options.tr>
            <options.td>
              <TioPreviewButton @onClick={{fn this.showPreviewModal result}} />
            </options.td>
            {{#let
              (safeFormatDate result.createdAt month="long" day="2-digit" year="numeric")
              as |date|
            }}
              <options.td title={{date}}>{{date}}</options.td>
            {{/let}}
            <options.td>{{result.bestPlanTableRowSummary}}</options.td>
          </options.tr>
        {{else}}
          <options.tr>
            <options.td colspan="3">
              {{t "common.recommendations.no_results"}}
            </options.td>
          </options.tr>
        {{/each}}
      </:tbody>
    </Table>

    <Modal
      @isOpen={{this.showPreview}}
      @onClose={{this.closePreviewModal}}
      @ariaLabel="PDF Preview"
      class="min-h-64"
      @size="full"
    >
      <:body>
        {{#if this.pdfSrc}}
          <div {{embedPdf this.pdfSrc}}></div>
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{false}}
          @onSubmit={{this.closePreviewModal}}
          @submitText="Close Preview"
        />
      </:footer>
    </Modal>
  </template>
}
